import { combineReducers } from 'redux'
import { selectorForSlice } from 'lp-redux-utils'
import { handleActions } from 'redux-actions'
import { createSelector } from 'reselect'
import * as apiActions from 'api-actions'
import { setOnSuccess } from 'lp-redux-api'
import { orderBy, negate } from 'lodash'
import {
  orderAscendingToOtherByKey,
  removeParentDisciplines,
  isIgnoredType,
} from 'utils'
import { SCHOOL_YEAR_STATES } from 'types'

import { reducer as homeReducer, reducerKey as homeReducerKey } from './home'
import { reducer as mapReducer, reducerKey as mapReducerKey } from './map'
import {
  reducer as partnerPortalReducer,
  reducerKey as partnerPortalReducerKey,
} from './partner-portal'
import {
  reducer as artistPortalReducer,
  reducerKey as artistPortalReducerKey,
} from './artist-portal'
import {
  reducer as schoolPortalReducer,
  reducerKey as schoolPortalReducerKey,
} from './school-portal'
import {
  reducer as educatorPortalReducer,
  reducerKey as educatorPortalReducerKey,
} from './educator-portal'
import {
  reducer as learningHubReducer,
  reducerKey as learningHubReducerKey,
} from './learning-hub/reducer'

const reducerKey = 'root'

const globalReducerKey = 'global'
const slice = 'root.global'

const select = selectorForSlice(slice)
const initialState = {}

const globalReducer = handleActions(
  {
    [apiActions.fetchDisciplines]: setOnSuccess('allDisciplines'),
    [apiActions.fetchResourceTypes]: setOnSuccess('resourceTypes'),
    [apiActions.fetchSchoolYears]: setOnSuccess('schoolYears'),
    [apiActions.fetchProgramTypes]: setOnSuccess('programTypes'),
    [apiActions.fetchLanguages]: setOnSuccess('languages'),
    [apiActions.fetchOutcomeTypes]: setOnSuccess('outcomeTypes'),
    [apiActions.fetchApproaches]: setOnSuccess('approaches'),
    [apiActions.fetchGrades]: setOnSuccess('grades'),
    [apiActions.fetchDemographics]: setOnSuccess('demographics'),
    [apiActions.fetchBadgeQuestions]: setOnSuccess('badgeQuestions'),
    [apiActions.fetchApplicationQuestions]: setOnSuccess(
      'applicationQuestions'
    ),
    [apiActions.fetchCaCommonCores]: setOnSuccess('caCommonCores'),
    [apiActions.fetchOrganizationOptions]: setOnSuccess('organizationOptions'),
  },
  initialState
)

const selectors = {
  allDisciplines: select('allDisciplines'),
  resourceTypes: select('resourceTypes'),
  schoolYears: select('schoolYears'),
  programTypes: select('programTypes'),
  languages: select('languages'),
  outcomeTypes: select('outcomeTypes'),
  approaches: select('approaches'),
  grades: select('grades'),
  demographics: select('demographics'),
  badgeQuestions: select('badgeQuestions'),
  applicationQuestions: select('applicationQuestions'),
  caCommonCores: select('caCommonCores'),
  organizationOptions: select('organizationOptions'),
}

selectors.disciplines = createSelector(
  [selectors.allDisciplines],
  function (allDisciplines) {
    if (!allDisciplines) return
    return removeParentDisciplines(allDisciplines)
  }
)

selectors.definedDisciplines = createSelector(
  [selectors.disciplines],
  function (disciplines) {
    if (!disciplines) return
    return disciplines.filter(negate(isIgnoredType))
  }
)

selectors.orderedProgramTypes = createSelector(
  [selectors.programTypes],
  function (programTypes) {
    return orderAscendingToOtherByKey(programTypes)
  }
)

// Order school years by most recent (DESC)
selectors.orderedSchoolYears = createSelector(
  [selectors.schoolYears],
  function (schoolYears) {
    if (!schoolYears) return
    return orderBy(schoolYears, 'position', 'desc')
  }
)

selectors.currentSchoolYear = createSelector(
  [selectors.schoolYears],
  function (schoolYears) {
    if (!schoolYears) return
    return schoolYears.find((schoolYear) => schoolYear.current)
  }
)

selectors.mostRecentPublishedSchoolYear = createSelector(
  [selectors.orderedSchoolYears],
  function (schoolYears) {
    if (!schoolYears) return
    return schoolYears.find(
      (schoolYear) => schoolYear.state === SCHOOL_YEAR_STATES.PUBLISHED
    )
  }
)

const reducer = combineReducers({
  [homeReducerKey]: homeReducer,
  [mapReducerKey]: mapReducer,
  [partnerPortalReducerKey]: partnerPortalReducer,
  [artistPortalReducerKey]: artistPortalReducer,
  [schoolPortalReducerKey]: schoolPortalReducer,
  [educatorPortalReducerKey]: educatorPortalReducer,
  [learningHubReducerKey]: learningHubReducer,
  [globalReducerKey]: globalReducer,
})

export { reducer, selectors, reducerKey }
